import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, get, child } from 'firebase/database';
import firebaseConfig from '../../config/firebase-config.js';
import useAuthStore from '../hooks/useAuthStore.js';

// Initialize Firebase app at module level (singleton pattern)
let app = null;
let db = null;

function getFirebaseDatabase() {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  if (!db) {
    db = getDatabase(app);
  }
  return db;
}

export default function ResourceCategories() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [categories, setCategories] = useState([]);

  // Get Firebase database instance
  const db = getFirebaseDatabase();

  // Determine user's curriculum and grade from stream
  const userStream = user?.stream || 'ICSE Class 10'; // Default to Class 10 if not set
  const isISC = userStream.includes('ISC');
  const isClass12 = userStream.includes('Class 12');
  const isClass11 = userStream.includes('Class 11');
  const isClass9 = userStream.includes('Class 9');
  const curriculum = isISC ? 'ISC' : 'ICSE';
  const gradeLevel = isClass12 ? 'Class 12' : isClass11 ? 'Class 11' : isClass9 ? 'Class 9' : 'Class 10';

  // Dynamic category configuration based on user's curriculum
  const getCategoryConfig = () => {
    if (isClass9) {
      return [
        {
          id: 'chemistry',
          title: 'Class 9 Chemistry',
          subtitle: 'Textbook & Workbook',
          icon: '🧪',
          color: 'from-green-500 to-emerald-500',
          filterCategories: ['Class 9 Chemistry', 'Chemistry Workbook']
        },
        {
          id: 'physics',
          title: 'Class 9 Physics',
          subtitle: 'Textbook & Workbook',
          icon: '⚛️',
          color: 'from-blue-500 to-cyan-500',
          filterCategories: ['Class 9 Physics', 'Physics Workbook']
        },
        {
          id: 'biology',
          title: 'Class 9 Biology',
          subtitle: 'Textbook & Workbook',
          icon: '🧬',
          color: 'from-pink-500 to-rose-500',
          filterCategories: ['Class 9 Biology', 'Biology Workbook']
        },
        {
          id: 'english',
          title: 'Class 9 English',
          subtitle: 'Literature Workbook & Grammar',
          icon: '📖',
          color: 'from-purple-500 to-indigo-500',
          filterCategories: ['Class 9 English', 'English Workbook', 'English Literature Workbook', 'English Grammar']
        }
      ];
    } else if (isISC && isClass12) {
      return [
        {
          id: 'chemistry',
          title: 'Class 12 Chemistry',
          subtitle: 'Textbook & Workbook',
          icon: '🧪',
          color: 'from-green-500 to-emerald-500',
          filterCategories: ['Class 12 Chemistry', 'Chemistry Workbook', 'ISC Chemistry']
        },
        {
          id: 'physics',
          title: 'Class 12 Physics',
          subtitle: 'Textbook & Workbook',
          icon: '⚛️',
          color: 'from-blue-500 to-cyan-500',
          filterCategories: ['Class 12 Physics', 'Physics Workbook', 'ISC Physics']
        },
        {
          id: 'biology',
          title: 'Class 12 Biology',
          subtitle: 'Textbook & Workbook',
          icon: '🧬',
          color: 'from-pink-500 to-rose-500',
          filterCategories: ['Class 12 Biology', 'Biology Workbook', 'ISC Biology']
        },
        {
          id: 'english',
          title: 'Class 12 English',
          subtitle: 'Literature Workbook & Grammar',
          icon: '📖',
          color: 'from-purple-500 to-indigo-500',
          filterCategories: ['Class 12 English', 'English Workbook', 'English Literature Workbook', 'English Grammar', 'ISC English']
        }
      ];
    } else if (isClass11) {
      return [
        {
          id: 'chemistry',
          title: 'Class 11 Chemistry',
          subtitle: 'Textbook & Workbook',
          icon: '🧪',
          color: 'from-green-500 to-emerald-500',
          filterCategories: ['Class 11 Chemistry', 'Chemistry Workbook', 'ISC Chemistry']
        },
        {
          id: 'physics',
          title: 'Class 11 Physics',
          subtitle: 'Textbook & Workbook',
          icon: '⚛️',
          color: 'from-blue-500 to-cyan-500',
          filterCategories: ['Class 11 Physics', 'Physics Workbook', 'ISC Physics']
        },
        {
          id: 'biology',
          title: 'Class 11 Biology',
          subtitle: 'Textbook & Workbook',
          icon: '🧬',
          color: 'from-pink-500 to-rose-500',
          filterCategories: ['Class 11 Biology', 'Biology Workbook', 'ISC Biology']
        },
        {
          id: 'english',
          title: 'Class 11 English',
          subtitle: 'Literature Workbook & Grammar',
          icon: '📖',
          color: 'from-purple-500 to-indigo-500',
          filterCategories: ['Class 11 English', 'English Workbook', 'English Literature Workbook', 'English Grammar', 'ISC English']
        }
      ];
    } else {
      // Default to Class 10 ICSE
      return [
        {
          id: 'chemistry',
          title: 'Class 10 Chemistry',
          subtitle: 'Textbook & Workbook',
          icon: '🧪',
          color: 'from-green-500 to-emerald-500',
          filterCategories: ['Class 10 Chemistry', 'Chemistry Workbook']
        },
        {
          id: 'physics',
          title: 'Class 10 Physics',
          subtitle: 'Textbook & Workbook',
          icon: '⚛️',
          color: 'from-blue-500 to-cyan-500',
          filterCategories: ['Class 10 Physics', 'Physics Workbook']
        },
        {
          id: 'biology',
          title: 'Class 10 Biology',
          subtitle: 'Textbook & Workbook',
          icon: '🧬',
          color: 'from-pink-500 to-rose-500',
          filterCategories: ['Class 10 Biology', 'Biology Workbook']
        },
        {
          id: 'english',
          title: 'Class 10 English',
          subtitle: 'Literature Workbook & Grammar',
          icon: '📖',
          color: 'from-purple-500 to-indigo-500',
          filterCategories: ['Class 10 English', 'English Workbook', 'English Literature Workbook', 'English Grammar']
        }
      ];
    }
  };

  useEffect(() => {
    async function fetchCategories() {
      try {
        const dbRef = ref(db);
        const snapshot = await get(child(dbRef, 'textbooks'));
        
        if (snapshot.exists()) {
          const books = Object.values(snapshot.val());
          const categoryConfig = getCategoryConfig();
          
          // Calculate category counts dynamically based on user's curriculum
          const categoryData = categoryConfig.map(config => ({
            ...config,
            count: books.filter(book => 
              config.filterCategories.includes(book.category)
            ).length
          }));
          
          setCategories(categoryData);
        } else {
          // Use default categories based on user's curriculum if no data exists
          setCategories(getCategoryConfig().map(config => ({ ...config, count: 0 })));
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Use default categories based on user's curriculum on error
        setCategories(getCategoryConfig().map(config => ({ ...config, count: 0 })));
      }
    }

    fetchCategories();
  }, [user]);

  const handleCategoryClick = (categoryId) => {
    navigate(`/resources/${categoryId}`);
  };

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Recommended Textbooks</h1>
              <p className="text-purple-400 text-sm">Select a Subject</p>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Choose Your Subject
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Browse highly-rated {curriculum} {gradeLevel} textbooks and workbooks organized by subject
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleCategoryClick(category.id)}
                className="group relative overflow-hidden rounded-3xl p-8 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl glass-card border border-white/10 hover:border-purple-500/30"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
                
                <div className="relative z-10">
                  <div className="text-6xl mb-4">{category.icon}</div>
                  <h3 className="text-2xl font-bold text-white mb-2">{category.title}</h3>
                  <p className="text-purple-400 text-sm mb-4">{category.subtitle}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">{category.count} resources available</span>
                    <div className="flex items-center gap-2 text-purple-400 font-medium group-hover:gap-3 transition-all">
                      <span>Browse</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
